import loadable from '@loadable/component';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
  PRODUCT_GROUP,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import cx from 'classnames';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import { useMBDContext } from '../common/MBD/useMBDContext';

import LogoImage from './LogoImage/LogoImage';

import type { ProviderLogosProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ProviderLogos';

import STYLES from './ProviderLogos.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const ProviderLogos = ({
  enableLogoShadow,
  hasSectionBackground,
  headerText,
  openLinkInNewTab,
  providerInfo,
  vertical,
}: ProviderLogosProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!providerInfo?.length) {
    return null;
  }

  return (
    <div
      data-tracking-element-id={rawComponentName}
      data-testid="provider-logos"
      data-tracking-common-params={buildMBDParam(rawComponentName)}
      className={cx(STYLES.ProviderLogos, {
        [STYLES.ProviderLogosSectionBackground]: hasSectionBackground,
      })}
    >
      {headerText && (
        <div
          className={STYLES.ProviderLogos__header}
          data-testid="provider-logos-header"
        >
          <BpkSectionHeader title={headerText} />
        </div>
      )}

      <div
        className={cx(STYLES.logos, {
          [STYLES.ProviderLogos__centered]:
            vertical === PRODUCT_GROUP.PACKAGES && providerInfo.length >= 5,
        })}
      >
        {providerInfo.map((provider, index) => {
          const image = <LogoImage {...provider} />;

          return (
            <div
              data-testid={`provider-logos-header-${index}`}
              data-tracking-element-id={`${rawComponentName}_item_${index}`}
              data-tracking-common-params={buildMBDParam(
                `${rawComponentName}_item`,
                {
                  provider_info: provider.providerName,
                },
              )}
              key={provider.providerName}
              className={cx(STYLES.ProviderLogos__providerLogo, {
                [STYLES.ProviderLogos__providerLogoShadow]: enableLogoShadow,
              })}
            >
              {provider.link ? (
                <a
                  className={STYLES.ProviderLogos__providerLogoLink}
                  aria-label={provider.providerName}
                  href={provider.link}
                  target={openLinkInNewTab ? '_blank' : ''}
                  rel="noreferrer"
                >
                  {image}
                </a>
              ) : (
                image
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default (props: ProviderLogosProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.PROVIDER_LOGOS,
      })
    }
  >
    <ProviderLogos {...props} />
  </IntersectionObserverWrapper>
);
